import React, { memo, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import styled from "styled-components";
import { Button, Menu, MenuItem, TextField } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useCurrentUser } from "store/users/selectors";
import { downloadVideoLikePreview } from "utils/methods/cloudflareVideoDownload";
import { useAlert } from '../alertSakeBar/alertProvider';

const MENU_ABOVE_MODAL_Z_INDEX = 1600;

const copyUrlMenuProps = {
  sx: { zIndex: MENU_ABOVE_MODAL_Z_INDEX },
  slotProps: {
    paper: {
      sx: { zIndex: MENU_ABOVE_MODAL_Z_INDEX },
    },
  },
};

const CloudFlarePreview = ({ iframeUrl , style={}, is_placement= false, autoplay = true , allowFullScreen = true, onChange = null, pageId = undefined }) => {
  const videoRef = useRef();
  const playerRef = useRef();
  const currentUser = useCurrentUser();
  const [downloading, setDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const showAlert = useAlert();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [isAddVideoMode, setIsAddVideoMode] = useState(false);

  useEffect(() => {
    if (iframeUrl && videoRef.current) {
      videoRef.current.load();
    }
  }, [iframeUrl]);
  
  const getVideoUrl = (iframeSrc) => {
    try {
      const url = new URL(iframeSrc);
      const pathParts = url.pathname.split('/').filter(part => part.length > 0);
      const videoId = pathParts[0]; 
      if (videoId && videoId.length > 10) { 
        return `https://${url.hostname}/${videoId}/watch`;
      }
      return iframeSrc;
    } catch (error) {
      console.error('Error extracting video URL:', error);
      return iframeSrc;
    }
  };

  const extractCloudflareVideoId = (url) => {
    try {
      if (url.includes('cloudflarestream.com')) {
        const match = url.match(/cloudflarestream\.com\/([a-zA-Z0-9]+)/);
        return match ? match[1] : null;
      }
      if (url.includes('videodelivery.net')) {
        const match = url.match(/videodelivery\.net\/([a-zA-Z0-9]+)/);
        return match ? match[1] : null;
      }
      if (url.includes('.m3u8')) {
        const match = url.match(/\/([a-zA-Z0-9]+)\/manifest\/video\.m3u8/);
        return match ? match[1] : null;
      }
      return null;
    } catch (error) {
      console.error('Error extracting Cloudflare video ID:', error);
      return null;
    }
  };

  const isCloudflareUrl = (url) => {
    return (
      url &&
      (url.includes('cloudflarestream.com') ||
        url.includes('videodelivery.net') ||
        url.includes('.m3u8'))
    );
  };

  const buildCloudflareHlsManifestUrl = (href) => {
    if (!href) return null;
    if (href.includes('.m3u8')) return href;
    let u;
    try {
      u = new URL(href);
    } catch {
      return null;
    }
    const uid = extractCloudflareVideoId(href);
    if (!uid) return null;
    if (u.hostname === 'iframe.videodelivery.net') {
      const base = `https://videodelivery.net/${uid}/manifest/video.m3u8`;
      const qs = u.searchParams.toString();
      return qs ? `${base}?${qs}` : base;
    }
    const base = `${u.origin}/${uid}/manifest/video.m3u8`;
    const qs = u.searchParams.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const resolveCloudflareEmbedHref = (src) => {
    if (!src) return '';
    const t = String(src).trim();
    if (t.includes('<iframe')) {
      return normalizeVideoUrlInput(t);
    }
    return t;
  };

  const getIframeSrc = (src) => {
    const href = resolveCloudflareEmbedHref(src);
    if (!href) return src;
    const isIframeLike =
      String(src).includes('iframe') ||
      href.includes('/iframe') ||
      href.includes('iframe.videodelivery.net');
    if (!isIframeLike) return href;
    try {
      const url = new URL(href);
      if (autoplay) {
        url.searchParams.set('autoplay', 'true');
      } else {
        url.searchParams.set('autoplay', 'false');
      }
      return url.toString();
    } catch (e) {
      console.error("Error building iframe src", e);
      return href;
    }
  };

  const handleOpenInNewTab = () => {
    const videoUrl = getVideoUrl(iframeUrl);
    window.open(videoUrl, '_blank', 'noopener,noreferrer');
  };
  const handleCopyUrl = async () => {
    if (!iframeUrl) return;
    
    try {
      await navigator.clipboard.writeText(iframeUrl);
      showAlert('Video URL copied to clipboard!', 'success');
    } catch (error) {
      console.error('Error copying URL:', error);
      showAlert('Failed to copy URL. Please try again.', 'error');
    }
  };

  const normalizeVideoUrlInput = (value) => {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';
    if (trimmed.includes('<iframe')) {
      const match = trimmed.match(/src\s*=\s*["']([^"']+)["']/i);
      return (match?.[1] || '').trim();
    }
    return trimmed;
  };

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleCopyUrlFromMenu = async () => {
    setIsAddVideoMode(false);
    handleMenuClose();
    await handleCopyUrl();
  };

  const handleAddVideoWithUrl = () => {
    handleMenuClose();
    setShowUrlInput(true);
    setUrlInput('');
    setIsAddVideoMode(true);
  };

  const handleUrlSubmit = () => {
    const trimmedUrl = normalizeVideoUrlInput(urlInput);
    if (!trimmedUrl) {
      showAlert('Please enter a valid URL', 'error');
      return;
    }

    try {
      new URL(trimmedUrl);
    } catch (e) {
      showAlert('Invalid URL. Please enter a valid video URL.', 'error');
      return;
    }

    setShowUrlInput(false);
    setUrlInput('');
    setIsAddVideoMode(false);

    if (onChange && typeof onChange === 'function') {
      try {
        onChange({ url: trimmedUrl, pageId: pageId || undefined });
      } catch (error) {
        console.error('Error in onChange callback:', error);
      }
    }

    showAlert('Video URL added successfully!', 'success');
  };

  const handleUrlInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleUrlSubmit();
    }
  };

  const handleDownload = async (event) => {
    if (!iframeUrl || downloading) return;

    event.currentTarget.blur();
    setDownloading(true);
    setDownloadStatus('preparing');

    try {
      const videoId = isCloudflareUrl(iframeUrl) ? extractCloudflareVideoId(iframeUrl) : null;
      const fileName = videoId ? `video-${videoId}.mp4` : 'video.mp4';

      await downloadVideoLikePreview({
        url: iframeUrl,
        fileName,
        showAlert,
        onProgress: (status) => setDownloadStatus(status),
      });
    } catch (error) {
      console.error('Error downloading video:', error);
      setDownloadStatus('error');
      showAlert(error.message || 'Failed to prepare download. Please try again.', 'error');
    } finally {
      setTimeout(() => {
        setDownloading(false);
        setDownloadStatus(null);
      }, 1000);
    }
  };

if(!iframeUrl) return null;
  const isAdmin = currentUser && currentUser.role === "owe_admin";
  const iframeAllow = autoplay
    ? "accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
    : "accelerometer; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share";

  const resolvedEmbedHref = resolveCloudflareEmbedHref(iframeUrl);
  const placementHlsUrl =
    is_placement &&
    iframeUrl.includes("iframe") &&
    resolvedEmbedHref &&
    isCloudflareUrl(resolvedEmbedHref)
      ? buildCloudflareHlsManifestUrl(resolvedEmbedHref)
      : null;

  return (
    <>
      {iframeUrl.includes("iframe") ? (
        <>
          {placementHlsUrl ? (
            <ReactPlayerDiv style={style} key={placementHlsUrl}>
              <ReactPlayer
                className="playerSection"
                ref={playerRef}
                url={placementHlsUrl}
                width="100%"
                height="100%"
                controls
                playing={autoplay}
                playsInline
                config={{
                  file: {
                    forceHLS: true,
                    attributes: { playsInline: true },
                  },
                }}
                onError={(e) => console.error("Video player error:", e)}
              />
            </ReactPlayerDiv>
          ) : (
            <StyledIframe
              key={iframeUrl}
              title="Cloudflare Stream video"
              src={getIframeSrc(iframeUrl)}
              style={style}
              allow={iframeAllow}
              allowFullScreen={allowFullScreen}
              referrerPolicy="strict-origin-when-cross-origin"
            />
          )}
          {(isAdmin || !is_placement) && (
            <AdminVideoActionsBar>
              {isAdmin && (
                <>
                  <AdminVideoActionButton
                    variant="outlined"
                    size="small"
                    startIcon={isAddVideoMode ? <AddToPhotosIcon /> : <ContentCopyIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleMenuOpen}
                  >
                    {isAddVideoMode ? 'Add video with URL' : 'Copy URL'}
                  </AdminVideoActionButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={handleMenuClose}
                    {...copyUrlMenuProps}
                  >
                    <MenuItem onClick={handleCopyUrlFromMenu}>
                      <ContentCopyIcon style={{ marginRight: 8, fontSize: 18 }} />
                      Copy URL
                    </MenuItem>
                    <MenuItem onClick={handleAddVideoWithUrl}>
                      <AddToPhotosIcon style={{ marginRight: 8, fontSize: 18 }} />
                      Add video with URL
                    </MenuItem>
                  </Menu>
                  <DownloadActionButton
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    onMouseDown={(e) => e.preventDefault()}
                    $downloading={downloading}
                    aria-disabled={downloading}
                  >
                    {downloading
                      ? (downloadStatus === 'preparing'
                          ? 'Preparing...'
                          : downloadStatus === 'processing'
                            ? 'Processing...'
                            : downloadStatus === 'ready'
                              ? 'Downloading...'
                              : 'Downloading...')
                      : 'Download'}
                  </DownloadActionButton>
                </>
              )}
              {!is_placement && (
                <AdminVideoActionButton
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  onClick={handleOpenInNewTab}
                >
                  Watch in new tab
                </AdminVideoActionButton>
              )}
            </AdminVideoActionsBar>
          )}

          {isAdmin && showUrlInput && (
              <UrlInputPopover>
                <TextField
                  placeholder="Paste video URL here"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={handleUrlInputKeyPress}
                  size="small"
                  autoFocus
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                  }}
                />
                <UrlInputActions>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowUrlInput(false);
                      setUrlInput('');
                      setIsAddVideoMode(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" onClick={handleUrlSubmit}>
                    Add
                  </Button>
                </UrlInputActions>
              </UrlInputPopover>
            )}
        </>
      ) : (
        <ReactPlayerDiv>
          <ReactPlayer
            className="playerSection"
            ref={playerRef}
            url={iframeUrl}
            width="100%"
            height="100%"
            controls={true}
            playing={autoplay}
            onError={(e) => console.error("Video player error:", e)}
          />

          {isAdmin && (
            <>
              <AdminVideoActionsBar>
                <AdminVideoActionButton
                  variant="outlined"
                  size="small"
                  startIcon={isAddVideoMode ? <AddToPhotosIcon /> : <ContentCopyIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={handleMenuOpen}
                >
                  {isAddVideoMode ? 'Add video with URL' : 'Copy URL'}
                </AdminVideoActionButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={handleMenuClose}
                  {...copyUrlMenuProps}
                >
                  <MenuItem onClick={handleCopyUrlFromMenu}>
                    <ContentCopyIcon style={{ marginRight: 8, fontSize: 18 }} />
                    Copy URL
                  </MenuItem>
                  <MenuItem onClick={handleAddVideoWithUrl}>
                    <AddToPhotosIcon style={{ marginRight: 8, fontSize: 18 }} />
                    Add video with URL
                  </MenuItem>
                </Menu>
                <DownloadActionButton
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  onMouseDown={(e) => e.preventDefault()}
                  $downloading={downloading}
                  aria-disabled={downloading}
                >
                  {downloading
                    ? (downloadStatus === 'preparing'
                        ? 'Preparing...'
                        : downloadStatus === 'processing'
                          ? 'Processing...'
                          : downloadStatus === 'ready'
                            ? 'Downloading...'
                            : 'Downloading...')
                    : 'Download'}
                </DownloadActionButton>
              </AdminVideoActionsBar>
            {showUrlInput && (
              <UrlInputPopover>
                <TextField
                  placeholder="Paste video URL here"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={handleUrlInputKeyPress}
                  size="small"
                  autoFocus
                  fullWidth
                  sx={{
                    '& .MuiInputBase-input': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                  }}
                />
                <UrlInputActions>
                  <Button
                    size="small"
                    onClick={() => {
                      setShowUrlInput(false);
                      setUrlInput('');
                      setIsAddVideoMode(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="small"   onClick={handleUrlSubmit}>
                    OK
                  </Button>
                </UrlInputActions>
              </UrlInputPopover>
            )}
            </>
          )}
          
        </ReactPlayerDiv>
      )}
    </>
  );
};

export default memo(CloudFlarePreview);

const ReactPlayerDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  min-height: 307px;
`;

const StyledIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  min-height: 307px;

  // @media (max-width: 900px){
  //   min-height: 0px;
  // }
  @media (max-width: 560px){
    min-height: 100%;
  }
`;

const AdminVideoActionsBar = styled.div`
  position: absolute;
  bottom: -35px;
  right: 1px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  z-index: 10;
  max-width: calc(100% - 8px);

  @media (max-width: 560px) {
    bottom: -66px;
    right: 5px;
    gap: 6px;
    flex-wrap: wrap;
  }
`;

const ACTION_TEAL = '#03989e';

const adminVideoActionButtonAttrs = {
  disableRipple: true,
  disableFocusRipple: true,
  color: 'primary',
};

const AdminVideoActionButton = styled(Button).attrs(adminVideoActionButtonAttrs)`
  position: static;
  flex-shrink: 0;
  background-color: rgba(0, 0, 0, 0.7) !important;
  color: ${ACTION_TEAL} !important;
  border: 1px solid ${ACTION_TEAL} !important;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 12px;
  white-space: nowrap;
  transition: none !important;
  box-shadow: none !important;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8) !important;
    border-color: ${ACTION_TEAL} !important;
    color: ${ACTION_TEAL} !important;
  }

  &:focus,
  &:focus-visible,
  &.Mui-focusVisible {
    outline: none !important;
    box-shadow: none !important;
    background-color: rgba(0, 0, 0, 0.7) !important;
    border-color: ${ACTION_TEAL} !important;
    color: ${ACTION_TEAL} !important;
  }

  &:active {
    background-color: rgba(0, 0, 0, 0.8) !important;
    border-color: ${ACTION_TEAL} !important;
    color: ${ACTION_TEAL} !important;
  }

  .MuiButton-startIcon,
  .MuiButton-endIcon,
  .MuiSvgIcon-root {
    color: ${ACTION_TEAL} !important;
  }

  .MuiTouchRipple-root {
    display: none;
  }

  @media (max-width: 560px) {
    padding: 4px 8px;
    font-size: 11px;
  }
`;

const DownloadActionButton = styled(AdminVideoActionButton)`
  min-width: 125px;
  justify-content: flex-start;

  ${(p) =>
    p.$downloading &&
    `
    cursor: wait;
    pointer-events: none;
    opacity: 1 !important;
    background-color: rgba(0, 0, 0, 0.7) !important;
    color: ${ACTION_TEAL} !important;
    border-color: ${ACTION_TEAL} !important;

    .MuiButton-startIcon,
    .MuiSvgIcon-root {
      color: ${ACTION_TEAL} !important;
    }
  `}
`;

const UrlInputPopover = styled.div`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.9);
  padding: 12px;
  border-radius: 8px;
  z-index: 1400;
  min-width: 320px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  @media (max-width: 560px) {
    min-width: 260px;
    right: 5px;
    bottom: 5px;
  }
`;

const UrlInputActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
`;
